import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Badge } from '../ui/Badge'
import { Package as PackageType } from '../../services/packages.service'
import { packagesService } from '../../services/packages.service'
import LoadingSpinner from '../LoadingSpinner'
import toast from 'react-hot-toast'
import { X, Plus, Trash2 } from 'lucide-react'

interface PackageFormProps {
  package?: PackageType | null
  regions: string[]
  onSave: () => void
  onCancel: () => void
}

interface FormData {
  name: string
  type: 'BASIC' | 'STANDARD' | 'PREMIUM' | 'CUSTOM'
  price: string
  description: string
  includes: string[]
  excludes: string[]
  isPopular: boolean
  isActive: boolean
  sortOrder: string
  maxDistance: string
  serviceAreas: string[]
}

const initialFormData: FormData = {
  name: '',
  type: 'BASIC',
  price: '',
  description: '',
  includes: [],
  excludes: [],
  isPopular: false,
  isActive: true,
  sortOrder: '0',
  maxDistance: '',
  serviceAreas: []
}

export default function PackageForm({ package: editingPackage, regions, onSave, onCancel }: PackageFormProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [loading, setLoading] = useState(false)
  const [newInclude, setNewInclude] = useState('')
  const [newExclude, setNewExclude] = useState('')

  useEffect(() => {
    if (editingPackage) {
      setFormData({
        name: editingPackage.name,
        type: editingPackage.type,
        price: editingPackage.price.toString(),
        description: editingPackage.description || '',
        includes: [...editingPackage.includes],
        excludes: [...editingPackage.excludes],
        isPopular: editingPackage.isPopular,
        isActive: editingPackage.isActive,
        sortOrder: editingPackage.sortOrder.toString(),
        maxDistance: editingPackage.maxDistance?.toString() || '',
        serviceAreas: [...editingPackage.serviceAreas]
      })
    }
  }, [editingPackage])

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const addInclude = () => {
    if (newInclude.trim()) {
      setFormData(prev => ({
        ...prev,
        includes: [...prev.includes, newInclude.trim()]
      }))
      setNewInclude('')
    }
  }

  const removeInclude = (index: number) => {
    setFormData(prev => ({
      ...prev,
      includes: prev.includes.filter((_, i) => i !== index)
    }))
  }

  const addExclude = () => {
    if (newExclude.trim()) {
      setFormData(prev => ({
        ...prev,
        excludes: [...prev.excludes, newExclude.trim()]
      }))
      setNewExclude('')
    }
  }

  const removeExclude = (index: number) => {
    setFormData(prev => ({
      ...prev,
      excludes: prev.excludes.filter((_, i) => i !== index)
    }))
  }

  const toggleServiceArea = (area: string) => {
    setFormData(prev => ({
      ...prev,
      serviceAreas: prev.serviceAreas.includes(area)
        ? prev.serviceAreas.filter(a => a !== area)
        : [...prev.serviceAreas, area]
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    if (!formData.name.trim()) {
      toast.error('Package name is required')
      return
    }

    if (!formData.price || isNaN(Number(formData.price)) || Number(formData.price) <= 0) {
      toast.error('Valid price is required')
      return
    }

    if (formData.includes.length === 0) {
      toast.error('At least one included service is required')
      return
    }

    try {
      setLoading(true)

      const packageData = {
        name: formData.name.trim(),
        type: formData.type,
        price: Number(formData.price),
        description: formData.description.trim(),
        includes: formData.includes,
        excludes: formData.excludes,
        isPopular: formData.isPopular,
        isActive: formData.isActive,
        sortOrder: Number(formData.sortOrder) || 0,
        maxDistance: formData.maxDistance ? Number(formData.maxDistance) : undefined,
        serviceAreas: formData.serviceAreas
      }

      if (editingPackage) {
        await packagesService.updatePackage(editingPackage.id, packageData)
        toast.success('Package updated successfully')
      } else {
        await packagesService.createPackage(packageData)
        toast.success('Package created successfully')
      }

      onSave()
    } catch (error) {
      console.error('Failed to save package:', error)
      toast.error('Failed to save package')
    } finally {
      setLoading(false)
    }
  }

  const packageTypeColors = {
    BASIC: 'bg-gray-100 text-gray-800',
    STANDARD: 'bg-blue-100 text-blue-800',
    PREMIUM: 'bg-green-100 text-green-800',
    CUSTOM: 'bg-purple-100 text-purple-800'
  }

  return (
    <div className="max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{editingPackage ? 'Edit Package' : 'Create New Package'}</span>
            <Button variant="outline" size="sm" onClick={onCancel}>
              <X className="h-4 w-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Package Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Standard Package"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Package Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="BASIC">Basic</option>
                  <option value="STANDARD">Standard</option>
                  <option value="PREMIUM">Premium</option>
                  <option value="CUSTOM">Custom</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Price (RM) *
                </label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  placeholder="1299"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Sort Order
                </label>
                <Input
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => handleInputChange('sortOrder', e.target.value)}
                  placeholder="0"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Max Distance (km)
                </label>
                <Input
                  type="number"
                  value={formData.maxDistance}
                  onChange={(e) => handleInputChange('maxDistance', e.target.value)}
                  placeholder="50"
                  min="0"
                />
                <p className="text-xs text-neutral-500 mt-1">Leave empty for nationwide coverage</p>
              </div>

              <div className="flex items-end space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.isPopular}
                    onChange={(e) => handleInputChange('isPopular', e.target.checked)}
                    className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-neutral-700">Popular Package</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-neutral-700">Active</span>
                </label>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Describe what this package includes..."
              />
            </div>

            {/* Service Areas */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Service Areas
              </label>
              <div className="space-y-2">
                {regions.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {regions.map(area => (
                      <button
                        key={area}
                        type="button"
                        onClick={() => toggleServiceArea(area)}
                        className={`px-3 py-1 rounded-full text-sm transition-colors ${
                          formData.serviceAreas.includes(area)
                            ? 'bg-primary-600 text-white'
                            : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                        }`}
                      >
                        {area}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-500">No regions available</p>
                )}
                {formData.serviceAreas.length === 0 && (
                  <p className="text-xs text-amber-600">Package will be available nationwide</p>
                )}
              </div>
            </div>

            {/* Included Services */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Included Services *
              </label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={newInclude}
                    onChange={(e) => setNewInclude(e.target.value)}
                    placeholder="Add included service..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInclude())}
                  />
                  <Button type="button" onClick={addInclude}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.includes.map((item, index) => (
                    <Badge key={index} variant="default" className="bg-green-100 text-green-800">
                      {item}
                      <button
                        type="button"
                        onClick={() => removeInclude(index)}
                        className="ml-2 text-green-600 hover:text-green-800"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            {/* Excluded Services */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Excluded Services
              </label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={newExclude}
                    onChange={(e) => setNewExclude(e.target.value)}
                    placeholder="Add excluded service..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExclude())}
                  />
                  <Button type="button" onClick={addExclude}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.excludes.map((item, index) => (
                    <Badge key={index} variant="outline" className="bg-red-50 text-red-800 border-red-200">
                      {item}
                      <button
                        type="button"
                        onClick={() => removeExclude(index)}
                        className="ml-2 text-red-600 hover:text-red-800"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            {/* Preview */}
            {formData.name && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Package Preview</h3>
                <div className="bg-neutral-50 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-neutral-900">{formData.name}</h4>
                      <Badge className={`mt-2 ${packageTypeColors[formData.type]}`}>
                        {formData.type}
                      </Badge>
                    </div>
                    <div className="text-2xl font-bold text-primary-600">
                      RM {Number(formData.price).toLocaleString()}
                    </div>
                  </div>
                  {formData.description && (
                    <p className="text-neutral-600 mb-4">{formData.description}</p>
                  )}
                  {formData.includes.length > 0 && (
                    <div className="mb-4">
                      <h5 className="font-medium text-neutral-900 mb-2">Includes:</h5>
                      <ul className="space-y-1">
                        {formData.includes.map((item, index) => (
                          <li key={index} className="text-sm text-neutral-600 flex items-center">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end space-x-4 pt-6 border-t">
              <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} loading={loading}>
                {editingPackage ? 'Update Package' : 'Create Package'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}